<template>
  <v-card class="alerts-dropdown">
    <v-list>
      <v-list-item
        :title="unreadAlertCount + ' ' + t('unread-alerts')"
        :subtitle="alerts.length + ' ' + t('alerts')"
      >
        <template v-slot:append>
          <v-icon @click="goToSettings" class="mr-4">mdi-cog</v-icon>
          <v-icon @click="refresh">mdi-reload</v-icon>
        </template>
      </v-list-item>

      <v-divider />

      <template v-if="!loaded">
        <v-skeleton-loader type="list-item-two-line" />
        <v-skeleton-loader type="list-item-two-line" />
        <v-skeleton-loader type="list-item-two-line" />
        <v-skeleton-loader type="list-item-two-line" />
      </template>

      <template v-else>
        <template
          v-for="(alert, index) in lastFiveAlerts"
          :key="alert.notificationId"
        >
          <v-list-item
            :value="alert"
            :title="alert.title"
            @click="selectAlert(alert)"
          >
            <template v-slot:prepend>
              <v-icon v-if="alert.isRead" large color="success">
                mdi-check-circle-outline
              </v-icon>

              <v-icon v-else large color="error">mdi-alert</v-icon>
            </template>

            <v-list-item-subtitle>
              {{ decodeHTMLEntities(alert.title) }}
            </v-list-item-subtitle>

            <v-list-item-subtitle>
              {{ decodeHTMLEntities(alert.subtitle) }}
            </v-list-item-subtitle>
          </v-list-item>

          <v-divider v-if="index < 4" />
        </template>
      </template>

      <v-list-item>
        <template v-slot:append>
          <v-btn @click="goToViewAll">{{ t('view-all') }}</v-btn>
        </template>
      </v-list-item>
    </v-list>
  </v-card>
</template>

<script setup>
import { toRefs, computed } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const emit = defineEmits(['selectAlert', 'refresh']);

const props = defineProps({
  unreadAlertCount: {
    type: Number,
  },
  alerts: {
    /** @type {import('vue').PropType<import('./alert_api').AlertObject[]>} */
    type: Array,
    default: () => [],
  },
  loaded: {
    type: Boolean,
  },
});
const { unreadAlertCount, alerts, loaded } = toRefs(props);

/**
 * @param {unknown} input
 */
function decodeHTMLEntities(input) {
  if (typeof input !== 'string') return '';

  let result = input
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&#39;/g, "'")
    .replace(/&#039;/g, "'");

  result = result.replace(/&[A-Za-z0-9#]*\.\.\.$/, '...');
  return result;
}

/**
 * @param {string} timestamp
 */
function formatDate(timestamp) {
  console.log('Formatting timestamp:', timestamp);
  const date = new Date(parseInt(timestamp));
  const now = new Date();

  const isSameDay =
    date.getDate() === now.getDate() &&
    date.getMonth() === now.getMonth() &&
    date.getFullYear() === now.getFullYear();

  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);

  const isYesterday =
    date.getDate() === yesterday.getDate() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getFullYear() === yesterday.getFullYear();

  /** @type {Intl.DateTimeFormatOptions} */
  const options = { hour: 'numeric', minute: 'numeric', hour12: true };

  if (isSameDay) {
    return `Today at ${date.toLocaleString('en-US', options)}`;
  } else if (isYesterday) {
    return `Yesterday at ${date.toLocaleString('en-US', options)}`;
  } else {
    return `${date.toLocaleDateString('en-US')} at ${date.toLocaleString('en-US', options)}`;
  }
}

const lastFiveAlerts = computed(() => {
  console.log('Alerts:', alerts.value);
  return alerts.value.slice(0, 5).map((alert) => ({
    ...alert,
    created: formatDate(alert.created_at),
  }));
});

/**
 * @param {import('./alert_api').AlertObject} alert
 */
function selectAlert(alert) {
  emit('selectAlert', alert);
}

function refresh() {
  emit('refresh');
}

function goToSettings() {
  window.location.href = '/admin/alerts/settings';
}

function goToViewAll() {
  window.location.href = '/admin/alerts';
}
</script>

<i18n>
  {
    "en": {
      "unread-alerts": "Unread Alerts",
      "alerts": "Alerts",
      "view-all": "View All"
    },
    "es": {
      "unread-alerts": "Alertas no Leídas",
      "alerts": "Alertas",
      "view-all": "Ver Todo"
    }
  }
</i18n>

<style scoped>
.alerts-dropdown {
  max-width: 400px;
}
</style>
